import { Phone, Mail, MapPin, Globe, Clock, Instagram, Facebook, Youtube } from "lucide-react";
import SectionHeading, { Reveal } from "@/components/SectionHeading";
import { BRANCHES, CONTACT, OPENING_HOURS, mapsEmbedUrl } from "@/data/content";

const SOCIAL_ICONS = { Instagram, Facebook, YouTube: Youtube };

const COMMON_CONTACTS = [
  { icon: Phone, label: "Call Us", value: CONTACT.phoneDisplay, href: CONTACT.phoneHref, testid: "contact-phone-link" },
  { icon: Mail, label: "Email Us", value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-email-link" },
  { icon: Globe, label: "Visit Us Online", value: CONTACT.websiteDisplay, href: CONTACT.websiteHref, testid: "contact-website-link", external: true },
];

const BranchContactCard = ({ branch }) => {
  const comingSoon = branch.status !== "OPEN";
  return (
    <div
      data-testid={`contact-card-${branch.id}`}
      className={`rounded-xl p-6 h-full flex flex-col ${
        comingSoon
          ? "bg-[#141414] border border-dashed border-[#FF5A1F]/50"
          : "bg-[#141414] border border-white/10 transition-all duration-300 hover:border-[#FF5A1F]/60 hover:shadow-[0_10px_30px_rgba(255,90,31,0.12)]"
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading text-2xl tracking-wider text-white uppercase">{branch.name}</h3>
        {comingSoon && (
          <span className="bg-[#FF5A1F]/20 text-[#FF5A1F] border border-[#FF5A1F]/60 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest animate-pulse">
            Opening Soon
          </span>
        )}
      </div>
      {comingSoon ? (
        <p className="mt-4 text-sm leading-relaxed text-[#D1D1D1]">
          Address, phone number and opening hours for our new branch will be announced soon.
        </p>
      ) : (
        <ul className="mt-5 space-y-3.5 text-sm text-[#D1D1D1]">
          <li className="flex items-start gap-2.5">
            <MapPin className="w-4 h-4 text-[#FF5A1F] mt-0.5 shrink-0" />
            {branch.address}
          </li>
          <li
            data-testid={`contact-hours-${branch.id}`}
            className="flex items-start gap-2.5"
          >
            <Clock className="w-4 h-4 text-[#FF5A1F] mt-0.5 shrink-0" />
            <span>
              {OPENING_HOURS.days}: {OPENING_HOURS.morning} &amp; {OPENING_HOURS.evening}
              <br />
              {OPENING_HOURS.sunday}
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <Phone className="w-4 h-4 text-[#FF5A1F] mt-0.5 shrink-0" />
            <a href={CONTACT.phoneHref} className="hover:text-[#FF5A1F] transition-colors">
              {CONTACT.phoneDisplay}
            </a>
          </li>
          <li className="flex items-start gap-2.5">
            <Mail className="w-4 h-4 text-[#FF5A1F] mt-0.5 shrink-0" />
            <a href={CONTACT.emailHref} className="hover:text-[#FF5A1F] transition-colors">
              {CONTACT.email}
            </a>
          </li>
          <li className="flex items-start gap-2.5">
            <Globe className="w-4 h-4 text-[#FF5A1F] mt-0.5 shrink-0" />
            <a
              href={CONTACT.websiteHref}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#FF5A1F] transition-colors"
            >
              {CONTACT.websiteDisplay}
            </a>
          </li>
        </ul>
      )}
    </div>
  );
};

export default function Contact() {
  const openBranches = BRANCHES.filter((b) => b.status === "OPEN");
  return (
    <section id="contact" data-testid="contact-section" className="bg-[#0A0A0A] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Contact"
          title="Visit"
          accent="F12 Fitness"
          subtext="Call, email or drop by any of our branches in Thiruvananthapuram — we'd love to help you start training."
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {COMMON_CONTACTS.map((c, i) => (
            <Reveal key={c.testid} delay={i * 0.08}>
              <a
                data-testid={c.testid}
                href={c.href}
                {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="flex items-center gap-4 bg-[#141414] border border-white/10 rounded-xl px-5 py-4 transition-all duration-300 hover:border-[#FF5A1F]/60 hover:shadow-[0_10px_30px_rgba(255,90,31,0.12)]"
              >
                <span className="w-10 h-10 rounded-md bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 flex items-center justify-center shrink-0">
                  <c.icon className="w-5 h-5 text-[#FF5A1F]" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#8E8E93]">
                    {c.label}
                  </span>
                  <span className="block text-sm font-semibold text-white">{c.value}</span>
                </span>
              </a>
            </Reveal>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {BRANCHES.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.1} className="h-full">
              <BranchContactCard branch={b} />
            </Reveal>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          {openBranches.map((b) => (
            <Reveal key={b.id}>
              <div data-testid={`contact-map-${b.id}`} className="rounded-xl overflow-hidden border border-[#FF5A1F]/40 bg-[#141414]">
                <iframe
                  title={`Map — ${b.name}`}
                  src={mapsEmbedUrl(b.address)}
                  loading="lazy"
                  className="w-full h-56 md:h-64 grayscale-[0.35] contrast-[1.05]"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <p className="px-4 py-3 text-xs sm:text-sm text-[#D1D1D1] flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                  {b.name}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col items-center gap-4">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8E8E93]">
              Follow F12 Fitness
            </p>
            <div className="flex items-center gap-4">
              {CONTACT.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.label];
                return (
                  <a
                    key={s.label}
                    data-testid={`social-${s.label.toLowerCase()}-link`}
                    href={s.href}
                    aria-label={`F12 FITNESS on ${s.label}`}
                    className="w-11 h-11 rounded-full bg-[#141414] border border-white/10 flex items-center justify-center text-[#D1D1D1] hover:text-white hover:bg-[#FF5A1F] hover:border-[#FF5A1F] transition-colors"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
            <p className="text-xs text-[#8E8E93]">Social links are placeholders — to be updated.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
